/**
 * ProductCarousel
 *
 * Horizontally scrollable product card carousel using native CSS scroll-snap.
 * Supports two usage modes:
 *   1. Children-based: pass <Slide> wrapped cards as children (preferred for home page sections).
 *   2. Products-based: pass ProductEntity[] via `products` prop (legacy, used by RecommendedProducts).
 *
 * Features: left/right arrows, touch/swipe via scroll-snap, partial-peek of next card,
 * optional "See all" header link, keyboard arrow-key navigation, loading skeleton state.
 */

'use client';

import { useRef, useState, useCallback, useEffect, type ReactNode, type KeyboardEvent } from 'react';
import { ProductCard } from '@/components/molecules/ProductCard/ProductCard';
import { ProductEntity } from '@/domain/entities/product.entity';
import {
  Container,
  Header,
  Title,
  SeeAllLink,
  TrackWrapper,
  Track,
  Slide,
  Arrow,
  SkeletonSlide,
  SkeletonImage,
  SkeletonText,
} from './ProductCarousel.styled';

export interface ProductCarouselProps {
  title: string;
  /** Legacy product array mode (used by RecommendedProducts) */
  products?: ProductEntity[];
  /** Children-based mode -- pass <Slide><ProductCard /></Slide> elements */
  children?: ReactNode;
  /** When set, renders a "See all" link in the header */
  seeAllPath?: string;
  seeAllLabel?: string;
  /** Accessible label for the carousel region */
  ariaLabel?: string;
  /** Show skeleton loading state */
  isLoading?: boolean;
  /** Number of skeleton cards to show while loading (default 4) */
  skeletonCount?: number;
}

const SKELETON_IDS = ['sk-1', 'sk-2', 'sk-3', 'sk-4', 'sk-5', 'sk-6', 'sk-7', 'sk-8'] as const;

const ChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

function LoadingSkeleton({ count = 4 }: Readonly<{ count?: number }>) {
  return (
    <>
      {SKELETON_IDS.slice(0, count).map((id) => (
        <SkeletonSlide key={id}>
          <SkeletonImage />
          <SkeletonText $width="80%" />
          <SkeletonText $width="40%" />
        </SkeletonSlide>
      ))}
    </>
  );
}

export function ProductCarousel({
  title,
  products,
  children,
  seeAllPath,
  seeAllLabel,
  ariaLabel,
  isLoading = false,
  skeletonCount = 4,
}: Readonly<ProductCarouselProps>) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, products, children]);

  const scroll = (direction: 'left' | 'right') => {
    const el = trackRef.current;
    if (!el) return;
    const scrollAmount = el.clientWidth * 0.75;
    el.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      scroll('left');
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      scroll('right');
    }
  };

  const hasLegacyProducts = products && products.length > 0;
  const hasContent = children || hasLegacyProducts;

  if (!isLoading && !hasContent) return null;

  return (
    <Container role="region" aria-label={ariaLabel || title}>
      <Header>
        <Title>{title}</Title>
        {seeAllPath && (
          <SeeAllLink to={seeAllPath}>{seeAllLabel || 'See All'}</SeeAllLink>
        )}
      </Header>
      <TrackWrapper>
        {canScrollLeft && (
          <Arrow $side="left" onClick={() => scroll('left')} aria-label="Scroll left">
            <ChevronLeft />
          </Arrow>
        )}
        <Track
          ref={trackRef}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          role="list"
        >
          {isLoading && <LoadingSkeleton count={skeletonCount} />}
          {!isLoading && children}
          {!isLoading && !children && products?.map((product) => (
            <Slide key={product.id} role="listitem">
              <ProductCard
                id={product.id}
                slug={product.slug}
                name={product.title}
                image={product.images[0]?.src ?? ''}
                price={product.price.display}
                compareAtPrice={product.compare_at_price?.display}
                rating={product.rating?.average}
                reviewCount={product.rating?.count}
              />
            </Slide>
          ))}
        </Track>
        {canScrollRight && (
          <Arrow $side="right" onClick={() => scroll('right')} aria-label="Scroll right">
            <ChevronRight />
          </Arrow>
        )}
      </TrackWrapper>
    </Container>
  );
}
